import { ReactNode, createContext, useEffect, useState } from "react";
import { useAuthContext } from "../hooks/use-auth-context.ts";
import { useProfileApi } from "../hooks/use-profile-api.ts";
import { ApiResponseType } from "../../api/types.ts";
import { IProfile } from "../../../interface/IProfile.ts";

type ProfileContextType = {
    profile: IProfile | null;
    userRoles: string[];
    isRoleValidated: boolean;
    needsInitialReview: boolean;
    isProfileLoaded: boolean;
    reloadProfile(): void;
};

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

/*
 * Roles that have to go through the "Erstprüfung" before they may use the
 * portal. Other (future) roles are never forced into the review, even when
 * is_role_validated is "0".
 */
const ROLES_REQUIRING_INITIAL_REVIEW = ['advisor'];

/*
 * Defensive default: a missing/unknown flag is treated as validated so that
 * existing users are never locked out. Only an explicit "0"/0/false counts as
 * "not yet validated".
 */
const deriveRoleValidated = (raw: unknown): boolean => {
    if (raw === null || raw === undefined) {
        return true;
    }
    return !(raw === "0" || raw === 0 || raw === false);
};

const deriveUserRoles = (raw: unknown): string[] => {
    if (Array.isArray(raw)) {
        return raw.map(role => String(role));
    }
    if (typeof raw === "string" && raw.length > 0) {
        return [raw];
    }
    return [];
};

function ProfileProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated } = useAuthContext();
    const { getProfile } = useProfileApi();

    const [profile, setProfile] = useState<IProfile | null>(null);
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [isRoleValidated, setIsRoleValidated] = useState(true);
    const [isProfileLoaded, setIsProfileLoaded] = useState(false);
    const [reloadTrigger, setReloadTrigger] = useState(0);

    const reloadProfile = () => setReloadTrigger(count => count + 1);

    const needsInitialReview =
        !isRoleValidated &&
        userRoles.some(role => ROLES_REQUIRING_INITIAL_REVIEW.includes(role.toLowerCase()));

    useEffect(() => {
        if (!isAuthenticated) {
            setProfile(null);
            setUserRoles([]);
            setIsRoleValidated(true);
            setIsProfileLoaded(false);
            return;
        }

        let cancelled = false;
        setIsProfileLoaded(false);

        getProfile().then(data => {
            if (cancelled) {
                return;
            }
            // eslint-disable-next-line
            const payload: any = data?.payload;
            if (data?.type === ApiResponseType.OK && payload && typeof payload === 'object') {
                setProfile(payload);
                setUserRoles(deriveUserRoles(payload.roles));
                setIsRoleValidated(deriveRoleValidated(payload.is_role_validated));
            }
            setIsProfileLoaded(true);
        }).catch(() => {
            if (!cancelled) {
                setIsProfileLoaded(true);
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, reloadTrigger]);

    return (
        <ProfileContext.Provider
            value={{
                profile,
                userRoles,
                isRoleValidated,
                needsInitialReview,
                isProfileLoaded,
                reloadProfile,
            }}
        >
            {children}
        </ProfileContext.Provider>
    );
}

export { ProfileProvider, ProfileContext };
